import { useCallback, useMemo } from "react";
import { useStore } from "@tanstack/react-form";
import type { GroupedSmartSurveyorElement } from "components/SmartSurveyor/ReviewPage/types";
import { getFormDefaultValues } from "../../ElementInspectorV3/utils";
import { resolveGroupColor } from "../../utils/resolveGroupColor";
import { useElementsCreationModeV3 } from "../../ElementsCreationModeV3";
import type { ElementCreationRow } from "../../ElementsCreationModeV3";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { createNewMarker } from "../useGroupSelectionV3/utils";
import type {
  CreatedMarkerResponse,
  UseCreationModeOrchestrationArgs,
} from "./types";

export const useCreationModeOrchestration = ({
  selectedPlan,
  selectedElement,
  interactionMode,
  creationContext,
  groupIds,
  byGroupId,
  onBulkCreateElements,
  floorPlanPanelHandlers,
  startCreatingElement,
  stopCreatingElement,
  setElementCreationStep,
  setElementCreationGroupName,
  setSelectedGroupLabel,
  setSelectedElementId,
  selectedElementForm,
}: UseCreationModeOrchestrationArgs) => {
  const handleCreateElements = useCallback(
    async (rows: ElementCreationRow[]) => {
      if (!selectedPlan || !creationContext) return;
      const { floorPlanId, pageIdByIndex } = selectedPlan;
      const pageId = pageIdByIndex.get(creationContext.pageIndex);

      const created: CreatedMarkerResponse[] =
        await api.bulkCreateQuantitySurveyorElementMarkers({
          floorPlanId,
          pageId,
          markers: rows.map(({ group_id, bbox, multiplier }) => ({
            elementTypeName: group_id,
            bboxX1: bbox.x1,
            bboxY1: bbox.y1,
            bboxX2: bbox.x2,
            bboxY2: bbox.y2,
            multiplier,
          })),
        });
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ELEMENTS_CREATED, {
        elementCount: created.length,
        isMultiAdd:
          interactionMode.kind === "creating-element" &&
          interactionMode.modifier === "multi-add",
      });
      // Resolve once per group so every element of a new group shares one color
      const colorByGroupName = new Map<string, [number, number, number]>();
      const getGroupColor = (groupName: string): [number, number, number] => {
        const resolvedColor = colorByGroupName.get(groupName);
        if (resolvedColor) return resolvedColor;
        const groupColor = resolveGroupColor({ groupName, byGroupId });
        colorByGroupName.set(groupName, groupColor);
        return groupColor;
      };

      const bulkElements = created.map(
        ({
          id: markerId,
          elementTypeName,
          bboxX1,
          bboxY1,
          bboxX2,
          bboxY2,
          elementTypeId,
          multiplier,
        }) => {
          const element: GroupedSmartSurveyorElement = createNewMarker({
            markerId: String(markerId),
            elementTypeName,
            bboxX1,
            bboxY1,
            bboxX2,
            bboxY2,
            multiplier,
            scaleFactor: 1,
            pageIndex: creationContext.pageIndex,
            floorPlanImage:
              selectedElement?.floorPlanImage ?? selectedPlan?.planImage ?? "",
            rgb: getGroupColor(elementTypeName),
          });
          return { groupLabel: elementTypeName, element, elementTypeId };
        },
      );
      onBulkCreateElements({ floorPlanId, elements: bulkElements });
      const firstCreated = bulkElements[0]?.element ?? null;
      if (firstCreated) {
        setSelectedGroupLabel(firstCreated.group_id);
        setSelectedElementId(firstCreated.id);
        selectedElementForm.reset(getFormDefaultValues(firstCreated));
        floorPlanPanelHandlers?.current?.zoomToBbox(firstCreated.bbox);
      }
    },
    [
      selectedPlan,
      creationContext,
      interactionMode,
      selectedElement?.floorPlanImage,
      byGroupId,
      onBulkCreateElements,
      setSelectedGroupLabel,
      setSelectedElementId,
      selectedElementForm,
      floorPlanPanelHandlers,
    ],
  );

  const handleCreationSaveAll = useCallback(
    async (rows: ElementCreationRow[]) => {
      await handleCreateElements(rows);
      stopCreatingElement();
    },
    [handleCreateElements, stopCreatingElement],
  );

  const handleCreationElementAdded = useCallback(
    (pos: { x: number; y: number }) => {
      floorPlanPanelHandlers?.current?.zoomToObject?.(pos);
    },
    [floorPlanPanelHandlers],
  );

  const creationStepForHook =
    interactionMode.kind === "creating-element" &&
    interactionMode.step === "adding-elements"
      ? "adding-elements"
      : "group-selection";

  const creationModeState = useElementsCreationModeV3({
    groupOptions: groupIds,
    creationStep: creationStepForHook,
    selectedGroupName: creationContext?.selectedGroupName ?? "",
    onCreationStepChange: setElementCreationStep,
    onSelectedGroupNameChange: setElementCreationGroupName,
    onSaveAll: handleCreationSaveAll,
    onCancel: stopCreatingElement,
    onElementAdded: handleCreationElementAdded,
    onElementSelected: handleCreationElementAdded,
  });

  const creationFormElements = useStore(
    creationModeState.form.store,
    (state) => state.values.elements,
  );

  const creationPreviewElements = useMemo<GroupedSmartSurveyorElement[]>(() => {
    if (!creationContext) return [];

    return creationFormElements.map((row) => ({
      id: row.id,
      name: "",
      layer: "",
      txt: "",
      rgb: [59, 130, 246] as [number, number, number],
      group_id: row.group_id,
      grid_cols: [],
      grid_rows: [],
      position_description: "",
      bbox: row.bbox,
      multiplier: row.multiplier,
      page: creationContext.pageIndex,
      status: "installed" as const,
      floorPlanImage:
        selectedElement?.floorPlanImage ?? selectedPlan?.planImage ?? "",
      _scaleFactor: 1,
      note: null,
    }));
  }, [
    creationFormElements,
    creationContext,
    selectedElement?.floorPlanImage,
    selectedPlan,
  ]);

  const creationActivePreviewElement = useMemo(
    () => creationPreviewElements[creationModeState.activeIndex] ?? null,
    [creationPreviewElements, creationModeState.activeIndex],
  );

  const openCreationMode = useCallback(() => {
    startCreatingElement({ pageIndex: selectedElement?.page ?? 0 });
    creationModeState.form.reset({ elements: [] });
    creationModeState.setActiveIndex(0);
  }, [creationModeState, selectedElement, startCreatingElement]);

  return {
    creationModeState,
    creationFormElements,
    creationPreviewElements,
    creationActivePreviewElement,
    openCreationMode,
    handleCreateElements,
  };
};
